// src/pages/SemanticHTMLPage.js
import React from 'react';
import { NavLink } from 'react-router-dom';
import PillButton from '../components/PillButton';

function SemanticHTMLPage() {
  return (
    <div>
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>Semantic HTML Guide</h1>
          <p className="lead">A resource for understanding and applying semantic HTML elements in web development</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="semantic-content" aria-labelledby="semantic-heading">
          <h2 id="semantic-heading">Basic Common Semantic HTML Elements</h2>
          <p>
            These are the most frequently used semantic HTML elements for structuring webpages. They provide meaning to content and improve accessibility and SEO.
          </p>

          {/* Chunk 1: <header> */}
          <div className="chunk">
            <h3>&lt;header&gt;</h3>
            <p>Represents introductory content or a masthead, typically at the top of a page or section.</p>
            <div className="code-snippet">
              <pre>
                {`<header>
    <h1>Welcome</h1>
</header>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;header&gt;</code> for site headers or section introductions, not just for styling purposes.
            </div>
          </div>

          {/* Chunk 2: <nav> */}
          <div className="chunk">
            <h3>&lt;nav&gt;</h3>
            <p>Defines a group of navigation links.</p>
            <div className="code-snippet">
              <pre>
                {`<nav>
    <ul>
        <li><a href="#home">Home</a></li>
        <li><a href="#about">About</a></li>
    </ul>
</nav>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Ensure <code>&lt;nav&gt;</code> contains only navigation links to improve screen reader navigation.
            </div>
          </div>

          {/* Chunk 3: <main> */}
          <div className="chunk">
            <h3>&lt;main&gt;</h3>
            <p>Contains the primary content of the page, unique to each document.</p>
            <div className="code-snippet">
              <pre>
                {`<main>
    <h1>Main Content</h1>
    <p>This is the core information.</p>
</main>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use only one <code>&lt;main&gt;</code> per page, and avoid nesting it inside other semantic elements like <code>&lt;article&gt;</code>.
            </div>
          </div>

          {/* Chunk 4: <article> */}
          <div className="chunk">
            <h3>&lt;article&gt;</h3>
            <p>Represents independent, self-contained content that could stand alone (e.g., a blog post).</p>
            <div className="code-snippet">
              <pre>
                {`<article>
    <h2>Article Title</h2>
    <p>Content here.</p>
</article>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;article&gt;</code> for content that can be syndicated, like blog posts or news articles.
            </div>
          </div>

          {/* Chunk 5: <section> */}
          <div className="chunk">
            <h3>&lt;section&gt;</h3>
            <p>Groups related content into thematic sections.</p>
            <div className="code-snippet">
              <pre>
                {`<section>
    <h2>Section Heading</h2>
    <p>Related content.</p>
</section>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Always include a heading (e.g., <code>&lt;h2&gt;</code>) inside a <code>&lt;section&gt;</code> for clarity.
            </div>
          </div>

          {/* Chunk 6: <aside> */}
          <div className="chunk">
            <h3>&lt;aside&gt;</h3>
            <p>Contains secondary content, such as sidebars, not essential to the main content.</p>
            <div className="code-snippet">
              <pre>
                {`<aside>
    <h3>Quick Facts</h3>
    <p>Additional info.</p>
</aside>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;aside&gt;</code> for content tangentially related to the main content, like side notes or ads.
            </div>
          </div>

          {/* Chunk 7: <footer> */}
          <div className="chunk">
            <h3>&lt;footer&gt;</h3>
            <p>Represents the footer of a page or section, often containing copyright or contact info.</p>
            <div className="code-snippet">
              <pre>
                {`<footer>
    <p>© 2025 My Site</p>
</footer>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Include metadata like author info or links in the footer for better context.
            </div>
          </div>

          {/* Chunk 8: Headings, Paragraphs, Lists, Links, Images */}
          <div className="chunk">
            <h3>Other Common Elements</h3>
            <p>These elements are also semantic and widely used for structuring content.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Headings -->
<h1>Main Heading</h1>
<h2>Subheading</h2>

<!-- Paragraph -->
<p>This is a paragraph.</p>

<!-- Lists -->
<ul>
    <li>Item 1</li>
    <li>Item 2</li>
</ul>
<ol>
    <li>Step 1</li>
    <li>Step 2</li>
</ol>

<!-- Link -->
<a href="https://example.com">Visit Example</a>

<!-- Image -->
<img src="image.jpg" alt="Description of image">`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Always provide an <code>alt</code> attribute for <code>&lt;img&gt;</code> to ensure accessibility.
            </div>
          </div>

          {/* Comprehensive List Section */}
          <h2>Comprehensive List of Semantic HTML Elements</h2>
          <p>
            This section provides a full reference of semantic HTML elements, including less common but valuable tags for specific use cases.
          </p>

          {/* Chunk 9: Structural Elements */}
          <div className="chunk">
            <h3>Structural Elements</h3>
            <p>These elements define the overall structure of an HTML document.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Root Element -->
<html lang="en">...</html>

<!-- Metadata -->
<head><title>Page Title</title></head>

<!-- Visible Content -->
<body><p>Content</p></body>

<!-- Generic Container -->
<div><p>Grouped content</p></div>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;div&gt;</code> only when no semantic element fits; prefer semantic tags for better structure.
            </div>
          </div>

          {/* Chunk 10: Text and Content Elements */}
          <div className="chunk">
            <h3>Text and Content Elements</h3>
            <p>These elements add meaning to text and content within your page.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Block Quote -->
<blockquote><p>Quote text</p></blockquote>

<!-- Inline Quote -->
<q>Short quote</q>

<!-- Citation -->
<cite>Author Name</cite>

<!-- Emphasized Text -->
<em>Important</em>

<!-- Strong Emphasis -->
<strong>Very important</strong>

<!-- Highlighted Text -->
<mark>Highlighted</mark>

<!-- Fine Print -->
<small>Disclaimer</small>

<!-- Abbreviation -->
<abbr title="HyperText Markup Language">HTML</abbr>

<!-- Contact Info -->
<address>123 Main St</address>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;em&gt;</code> and <code>&lt;strong&gt;</code> for semantic emphasis, not just visual styling.
            </div>
          </div>

          {/* Chunk 11: List Elements */}
          <div className="chunk">
            <h3>List Elements</h3>
            <p>These elements create structured lists for terms, definitions, or menus.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Description List -->
<dl>
    <dt>Term</dt>
    <dd>Definition</dd>
</dl>

<!-- Menu List -->
<menu>
    <li><button>Action</button></li>
</menu>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;dl&gt;</code> for glossaries or metadata lists, not for general content grouping.
            </div>
          </div>

          {/* Chunk 12: Media Elements */}
          <div className="chunk">
            <h3>Media Elements</h3>
            <p>These elements embed and annotate media content.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Figure with Caption -->
<figure>
    <img src="img.jpg" alt="">
    <figcaption>Caption</figcaption>
</figure>

<!-- Responsive Image -->
<picture>
    <source srcset="large.jpg" media="(min-width: 600px)">
    <img src="small.jpg" alt="Image">
</picture>

<!-- Video -->
<video controls><source src="video.mp4" type="video/mp4"></video>

<!-- Audio -->
<audio controls><source src="audio.mp3" type="audio/mp3"></audio>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Always include fallback content inside <code>&lt;video&gt;</code> and <code>&lt;audio&gt;</code> for unsupported browsers.
            </div>
          </div>

          {/* Chunk 13: Data and Code Elements */}
          <div className="chunk">
            <h3>Data and Code Elements</h3>
            <p>These elements represent data, code, or progress metrics.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Code Snippet -->
<code>let x = 5;</code>

<!-- Preformatted Text -->
<pre>
    Line 1
    Line 2
</pre>

<!-- Date/Time -->
<time datetime="2025-03-22">March 22, 2025</time>

<!-- Scalar Value -->
<meter min="0" max="100" value="75">75%</meter>

<!-- Progress -->
<progress max="100" value="50">50%</progress>`}
              </pre>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Use <code>&lt;pre&gt;</code> with <code>&lt;code&gt;</code> for multi-line code snippets to preserve formatting.
            </div>
          </div>

          {/* Chunk 14: Interactive Elements */}
          <div className="chunk">
            <h3>Interactive Elements</h3>
            <p>These elements enable user interaction.</p>
            <div className="code-snippet">
              <pre>
                {`<!-- Button -->
<button type="button">Click Me</button>`}
              </pre>
            </div>
            <div className="text-center mt-3">
              <NavLink to="/html">
                <PillButton>Back to HTML Guide</PillButton>
              </NavLink>
            </div>
            <div className="tip">
              <strong>Tip:</strong> Always specify <code>type="button"</code> for buttons to prevent default form submission behavior.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SemanticHTMLPage;